import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { default as Picker, registerLocale } from 'react-datepicker';
import type { Locale } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { cn } from '@shared/utils';
import { Select } from '@shared/widgets';

registerLocale('ru', ru as unknown as Locale);

interface DateRangePickerInputProps {
  className?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  readOnly?: boolean;
  dateFrom?: Dayjs;
  dateTo?: Dayjs;
  onChange?: (dateFrom?: Dayjs, dateTo?: Dayjs) => void;
}

export const DateRangePickerInput = (props: DateRangePickerInputProps) => {
  const [month, setMonth] = useState<number>(props.dateFrom?.month() ?? dayjs().month());
  const [year, setYear] = useState<number>(props.dateFrom?.year() ?? dayjs().year());

  const { i18n } = useTranslation();

  return (
    <div className={cn('flex flex-col justify-center', props.className)}>
      <div className='mb-2.5 flex items-center gap-4'>
        <Select
          className='min-w-[140px]'
          items={Array.from({ length: 12 }).map((_, idx) => ({
            label: dayjs()
              .locale(i18n.language || 'en')
              .month(idx)
              .format('MMMM'),
            value: idx,
          }))}
          readOnly={props.readOnly}
          value={month}
          onChange={(value) => setMonth(value!)}
        />
        <Select
          className='min-w-[100px]'
          items={Array.from({ length: 12 }).map((_, idx) => {
            const yearOption = dayjs().year() - 5 + idx;
            return { label: yearOption.toString(), value: yearOption };
          })}
          readOnly={props.readOnly}
          value={year}
          onChange={(value) => setYear(value!)}
        />
      </div>
      <Picker
        inline
        selectsRange
        locale={i18n.language || 'en'}
        minDate={props.minDate?.toDate()}
        maxDate={props.maxDate?.toDate()}
        openToDate={dayjs().year(year).month(month).toDate()}
        readOnly={props.readOnly}
        startDate={props.dateFrom?.toDate()}
        endDate={props.dateTo?.toDate()}
        onChange={(dates) => {
          if (props.readOnly) return;

          const startDate = dates[0];
          const endDate = dates[1];
          const dateFrom = startDate != null ? dayjs(startDate) : undefined;
          const dateTo = endDate != null ? dayjs(endDate) : undefined;

          props.onChange?.(dateFrom, dateTo);
        }}
        onMonthChange={(newMonth) => {
          setMonth(newMonth.getMonth());
          setYear(newMonth.getFullYear());
        }}
      />
    </div>
  );
};
